import type { CalendarSpan } from './Calendar.types'
import moment from 'moment'
import { charterCalendarDayKey } from 'lib/maldivesCharter/charterCalendarDayKey'
import {
  isFlightSavedItemData,
  isLeCruiseSavedItemData,
  isMarkedDefaultTransport,
  type TransportConnection,
  type TransportOption,
  type TripItineraryItem,
  type TripStop,
} from 'tripPlanner/api/types'
import { TRIP_LIST_CATEGORY_COLOURS } from 'tripPlanner/pages/chat/ChatTripPlannerPage/components/ListView/tripListCategoryColours'
import { sortTripStopsByItineraryOrder } from 'tripPlanner/utils/itineraryStopOrder'
import { resolveStopDuration } from 'tripPlanner/utils/stopDuration'
import { isHotelSavedData } from 'tripPlanner/utils/savedItemDataGuards'

export interface StopCalendarColours {
  foreground: string
  background: string
}

export const STOP_CALENDAR_COLOURS: ReadonlyArray<StopCalendarColours> = [
  { foreground: '#E8A44E', background: '#E8A44E26' },
  { foreground: '#599BF2', background: '#599BF226' },
  { foreground: '#80755A', background: '#80755A26' },
  { foreground: '#0F4C4C', background: '#0F4C4C26' },
  { foreground: '#C94922', background: '#C9492226' },
  { foreground: '#1DAF43', background: '#1DAF4326' },
]

export function getStopCalendarColours(index: number): StopCalendarColours {
  return STOP_CALENDAR_COLOURS[index % STOP_CALENDAR_COLOURS.length]
}

/*
 * Read from the list view's palette rather than restating it. These were three
 * verbatim copies of its hotel / transport / entertainment entries, so a change
 * there silently stopped matching here.
 */
function getCalendarItemColours(itemType: TripItineraryItem['type']): StopCalendarColours {
  switch (itemType) {
    case 'ACCOMMODATION': return TRIP_LIST_CATEGORY_COLOURS.hotel
    case 'FLIGHT':
    case 'CRUISE':
    case 'TRAIN':
    case 'FERRY':
    case 'BUS':
    case 'OTHER_TRANSPORT': return TRIP_LIST_CATEGORY_COLOURS.transport
    default: return TRIP_LIST_CATEGORY_COLOURS['entertainment-and-leisure']
  }
}

export interface StopSpanData {
  stopId: number
  placeName: string
  colours: StopCalendarColours
  hasSharedStartBoundary: boolean
  hasSharedEndBoundary: boolean
}

export interface BookedAccommodationSpanData {
  kind: 'booked-accommodation'
  itemId: number
  label: string
  colours: StopCalendarColours
}

/**
 * A leg too short to need a bar: a flight that takes off and lands on the same
 * day, or a drive the itinerary never gave dates to. It marks the day it
 * happens on rather than claiming a stretch of the calendar.
 */
export interface TransportMarker {
  id: string
  itemId: number
  date: string
  label: string
  itemType: TripItineraryItem['type']
  colours: StopCalendarColours
}

/** A flight, cruise or other transport item long enough to need a bar. */
export interface TransportSpanData {
  kind: 'transport'
  itemId: number
  label: string
  itemType: TripItineraryItem['type']
  colours: StopCalendarColours
}

export type StopCalendarSpanData =
  | StopSpanData
  | BookedAccommodationSpanData
  | TransportSpanData

export interface StopCalendarChip {
  id: string
  itemId: number
  label: string
  itemType: TripItineraryItem['type']
  colours: StopCalendarColours
}

export interface StopCalendarDayItem extends StopCalendarChip {
  startTime?: string
  stopName: string
  imageId?: string
}

/**
 * The day a stop's bar is drawn as ending on.
 *
 * Bars run from the middle of their first day to the middle of their last, so
 * a stop whose start and end land on the same day would come out with no width
 * at all. That shape is how a stop nobody has booked a night in yet is
 * recorded -- Melbourne, arrived at on the 11th and nothing else known -- and
 * it still means a night is spent there. It is drawn as that night: midday on
 * the 11th through midday on the 12th.
 */
function toRenderedEndDate(from: string, to: string): string {
  return from === to
    ? moment(from, 'YYYY-MM-DD').add(1, 'day').format('YYYY-MM-DD')
    : to
}

/**
 * The other stops already placed on the calendar, so someone choosing dates
 * for Sydney can see that Melbourne already occupies the 10th to the 13th.
 * The stop being edited is included but flagged, so it can be drawn
 * differently rather than filtered out and losing its place in the lanes.
 */
/** End day for a stop that carries a start and a night count but no end date. */
function deriveEndFromNights(stop: TripStop): string | undefined {
  const from = charterCalendarDayKey(stop.startDate)
  if (!from || stop.endDate) return undefined

  const duration = resolveStopDuration(stop)
  if (!duration) return undefined

  return moment(from, 'YYYY-MM-DD').add(duration.nightCount, 'days').format('YYYY-MM-DD')
}

export function buildStopSpans(
  stops: ReadonlyArray<TripStop> | undefined,
  _currentStopId?: number,
): Array<CalendarSpan<StopSpanData>> {
  if (!stops?.length) return []

  const orderedStops = sortTripStopsByItineraryOrder([...stops])
    .filter(stop => stop.stopType !== 'ORIGIN')

  /*
   * Colour follows a stop's place in the whole itinerary rather than among
   * the dated ones alone. The summary rail lists every destination and
   * colours them by that order, so indexing the bars over a shorter, dated
   * only list hands the same stop two different colours the moment an
   * undated one sits before it. A stop with no dates simply reserves a
   * colour no bar draws.
   */
  const colourIndexByStopId = new Map(orderedStops.map((stop, index) => [stop.id, index]))

  /*
   * Normalise once, here. The API types these as plain strings, and a stop
   * carrying '2026-10-09T00:00:00.000Z' next to one carrying '2026-10-09' is
   * the same calendar day -- but every comparison below is `===`, and a raw
   * mismatch reads as "no changeover", stacking two bars that should split a
   * day. Parsing with the input's own offset keeps the day the source meant.
   */
  const datedStops = orderedStops
    .map(stop => ({
      stop,
      from: charterCalendarDayKey(stop.startDate),
      /*
       * The contract types startDate and endDate independently optional, and a
       * stop with a start and no end used to be filtered out entirely -- no
       * bar, and its month never mounted. `resolveStopDuration` is the
       * codebase's existing answer to that shape: it falls back to
       * `numberOfNights`, which TripStop carries.
       */
      to: charterCalendarDayKey(stop.endDate) ?? deriveEndFromNights(stop),
    }))
    .filter((entry): entry is typeof entry & { from: string, to: string } => (
      Boolean(entry.from && entry.to && entry.from <= entry.to)
    ))
    /*
     * Normalise to the drawn dates before anything reads them. Changeovers,
     * lane packing and the bar geometry all have to agree on where a stop
     * ends, and a nightless stop's drawn end is the day after its recorded one.
     */
    .map(entry => ({ ...entry, to: toRenderedEndDate(entry.from, entry.to) }))

  /*
   * Neighbours come from date order, not sequence order. `sortTripStopsByItineraryOrder`
   * sorts on `stopSeq` alone, while `assignSpanLanes` packs by date -- so an
   * itinerary whose stopSeq has drifted from its dates (a streamed `stop_added`
   * splices by seq and never re-sequences) would compute a changeover against
   * one neighbour and pack lanes against another. Same input, different render.
   */
  const byDate = [...datedStops].sort((a, b) => (
    a.from < b.from ? -1 : a.from > b.from ? 1 : (a.stop.stopSeq - b.stop.stopSeq)
  ))
  const dateOrderById = new Map(byDate.map((entry, index) => [entry.stop.id, index]))

  return datedStops.map(({ stop, from, to }, index): CalendarSpan<StopSpanData> => {
    const dateIndex = dateOrderById.get(stop.id) ?? index
    const previousStop = byDate[dateIndex - 1]
    const nextStop = byDate[dateIndex + 1]
    const hasSharedStartBoundary = previousStop?.to === from
    const hasSharedEndBoundary = nextStop?.from === to

    return {
      id: `stop-${stop.id}`,
      from,
      to,
      data: {
        stopId: stop.id,
        placeName: stop.placeName || stop.placeCountry || '',
        colours: getStopCalendarColours(colourIndexByStopId.get(stop.id) ?? index),
        hasSharedStartBoundary,
        hasSharedEndBoundary,
      },
    }
  })
}

function toChip(item: TripItineraryItem): StopCalendarChip {
  return {
    id: `item-${item.id}`,
    itemId: item.id,
    label: item.name,
    itemType: item.type,
    colours: getCalendarItemColours(item.type),
  }
}

function getBookedAccommodationDateRange(
  item: TripItineraryItem,
  fallbackCheckIn: string,
): { from: string, to: string } | undefined {
  if (item.type !== 'ACCOMMODATION' || !('isBooked' in item) || !item.isBooked) return undefined

  const savedItemData = isHotelSavedData(item.savedItemData) ? item.savedItemData : undefined

  const itemStartDate = 'startDate' in item && typeof item.startDate === 'string' ? item.startDate : undefined
  const itemEndDate = 'endDate' in item && typeof item.endDate === 'string' ? item.endDate : undefined
  const bookedRoom = savedItemData?.bookedRooms?.find(
    (room): room is typeof room & { checkIn: string, checkOut: string } => (
      'checkIn' in room
      && 'checkOut' in room
      && typeof room.checkIn === 'string'
      && typeof room.checkOut === 'string'
    ),
  )
  const checkIn = savedItemData?.checkIn ?? bookedRoom?.checkIn ?? itemStartDate ?? fallbackCheckIn
  const derivedCheckOut = typeof item.totalDays === 'number' && item.totalDays > 1
    ? moment(checkIn).add(item.totalDays - 1, 'days').format('YYYY-MM-DD')
    : undefined
  const checkOut = savedItemData?.checkOut ?? bookedRoom?.checkOut ?? itemEndDate ?? derivedCheckOut
  if (!checkIn || !checkOut || checkOut < checkIn) return undefined

  return { from: checkIn, to: checkOut }
}

export function buildBookedAccommodationSpans(
  stops: ReadonlyArray<TripStop> | undefined,
): Array<CalendarSpan<BookedAccommodationSpanData>> {
  if (!stops?.length) return []

  const spansByItemId = new Map<string, CalendarSpan<BookedAccommodationSpanData>>()
  for (const stop of stops) {
    if (stop.stopType === 'ORIGIN') continue

    for (const day of stop.days) {
      const dayDate = day.date
      if (!dayDate || !day.items?.length) continue

      for (const item of day.items) {
        const dateRange = getBookedAccommodationDateRange(item, dayDate)
        if (!dateRange) continue

        const id = `booked-accommodation-${item.id}`
        if (spansByItemId.has(id)) continue

        spansByItemId.set(id, {
          id,
          from: dateRange.from,
          to: dateRange.to,
          laneOrder: 1,
          data: {
            kind: 'booked-accommodation',
            itemId: item.id,
            label: item.name,
            colours: getCalendarItemColours('ACCOMMODATION'),
          },
        })
      }
    }
  }

  return [...spansByItemId.values()]
}

/*
 * Everything that carries someone between two stops. A stay is drawn by its
 * stop bar; the days in between were blank until these, which is where a
 * red-eye or a two-night cruise actually lives.
 *
 * `transportOption.type` is a bare string on the contract, so anything
 * unrecognised is treated as generic transport rather than dropped.
 */
const TRANSPORT_ITEM_TYPES: ReadonlySet<string> = new Set([
  'FLIGHT', 'CRUISE', 'TRAIN', 'FERRY', 'BUS', 'OTHER_TRANSPORT',
])

function toTransportItemType(type: string): TripItineraryItem['type'] {
  return TRANSPORT_ITEM_TYPES.has(type)
    ? type as TripItineraryItem['type']
    : 'OTHER_TRANSPORT'
}

/**
 * The one option a connection is actually planned to use.
 *
 * A connection lists the alternatives side by side -- fly, drive or take the
 * train to Adelaide -- and only the chosen one belongs on the calendar.
 * Drawing all three would put three bars across the same days and imply an
 * itinerary nobody planned. A booking wins, then the default, then a lone
 * option that has no alternatives to lose to.
 */
function getPlannedTransportOption(connection: TransportConnection): TransportOption | undefined {
  return connection.options.find(option => option.status === 'BOOKED')
    ?? connection.options.find(option => isMarkedDefaultTransport(option.savedItemData))
    ?? (connection.options.length === 1 ? connection.options[0] : undefined)
}

/**
 * The days a transport option departs and arrives on.
 *
 * Flights carry an explicit arrival date because a red-eye lands on the next
 * one, with the timestamps as the fallback for an older hydration that wrote
 * the day only as part of the time. A cruise carries its dates on the
 * departure it was booked against, and failing that on the schedule preview
 * or its own night count.
 */
function getTransportOptionDates(
  option: TransportOption,
): { from?: string, to?: string } {
  const data = option.savedItemData
  let from: string | undefined
  let to: string | undefined

  if (isFlightSavedItemData(data)) {
    from = charterCalendarDayKey(data.departureDate ?? data.departureTime)
    to = charterCalendarDayKey(data.arrivalDate ?? data.arrivalTime)
  } else if (isLeCruiseSavedItemData(data)) {
    from = charterCalendarDayKey(data.departure?.departureDate)
    to = charterCalendarDayKey(data.departure?.arrivalDate)
    if (from && !to && typeof data.numberOfNights === 'number') {
      to = moment(from, 'YYYY-MM-DD').add(data.numberOfNights, 'days').format('YYYY-MM-DD')
    }
  }

  const preview = option.cruiseSchedulePreview
  return {
    from: from ?? charterCalendarDayKey(preview?.departure?.localDate),
    to: to ?? charterCalendarDayKey(preview?.arrival?.localDate),
  }
}

/**
 * The day a leg happens on when it has no span of its own.
 *
 * Its own departure day if it saved one; failing that the changeover between
 * the two stops it connects, which is where the journey sits whether or not
 * anyone has put a time on it. A drive between two cities is saved with a
 * duration and nothing else, and the changeover is the only day it can mean.
 */
function getTransportOptionDay(
  option: TransportOption,
  connection: TransportConnection,
  stopDaysById: ReadonlyMap<number, StopDays>,
): string | undefined {
  return getTransportOptionDates(option).from
    // The leg leaves as the stop it departs hands over. Failing that -- a stop
    // with no dates at all -- it arrives as the next one begins, which is the
    // same moment approached from the other side.
    ?? stopDaysById.get(connection.departureStopId)?.end
    ?? stopDaysById.get(connection.arrivalStopId)?.start
}

interface StopDays {
  start?: string
  /** The day the stop's bar ends on, which is not always the one it records. */
  end?: string
}

/**
 * The days each stop is drawn between, keyed by stop id.
 *
 * `end` is the drawn end, normalised through `toRenderedEndDate` exactly as
 * `buildStopSpans` does. Reading `endDate` raw would put a marker inside the
 * stay it leaves: a stop with no nights records the 11th but is drawn through
 * to midday on the 12th, and the 12th is where it actually hands over.
 */
function buildStopDaysById(stops: ReadonlyArray<TripStop>): Map<number, StopDays> {
  const stopDays = new Map<number, StopDays>()
  for (const stop of stops) {
    const start = charterCalendarDayKey(stop.startDate)
    const recordedEnd = charterCalendarDayKey(stop.endDate) ?? deriveEndFromNights(stop)
    /*
     * A stop with a start and nothing else has an unknown length, not a
     * nightless one -- its start day is the most that can be claimed, and
     * expanding it would assert a night nothing recorded.
     */
    const end = start && recordedEnd ? toRenderedEndDate(start, recordedEnd) : recordedEnd ?? start
    if (start !== undefined || end !== undefined) stopDays.set(stop.id, { start, end })
  }
  return stopDays
}

export interface TransportOverlays {
  /** Legs long enough to draw across the days they cover. */
  spans: Array<CalendarSpan<TransportSpanData>>
  /** Legs that fit inside one day, keyed by that day. */
  markersByDate: Map<string, Array<TransportMarker>>
}

/**
 * The transport between stops, as bars where it runs overnight and as a marker
 * on its own day where it does not.
 *
 * Read off `stop.transports` rather than the stop's days: a flight or a cruise
 * is a connection between two stops, and never appears among the items hanging
 * off a day the way a booked hotel or an experience does.
 *
 * Not gated on being booked, unlike the accommodation bars. An unbooked hotel
 * is already covered by the stop it sits in, while an unbooked flight is the
 * only thing that accounts for the day between two stops -- left out, the
 * journey is simply missing from the itinerary.
 */
export function buildTransportOverlays(
  stops: ReadonlyArray<TripStop> | undefined,
): TransportOverlays {
  const spans = new Map<string, CalendarSpan<TransportSpanData>>()
  const markersByDate = new Map<string, Array<TransportMarker>>()
  if (!stops?.length) return { spans: [], markersByDate }

  const stopDaysById = buildStopDaysById(stops)

  for (const stop of stops) {
    for (const connection of stop.transports ?? []) {
      const option = getPlannedTransportOption(connection)
      if (!option) continue

      const id = `transport-${option.itemId}`
      const itemType = toTransportItemType(option.type)
      // The connection names the journey -- "Sydney → Melbourne" -- which is
      // what a bar this wide can say. The offer's own name is far too long for
      // it, and the itinerary already carries that.
      const label = connection.name || option.name || option.label
      const colours = getCalendarItemColours(itemType)

      const { from, to } = getTransportOptionDates(option)
      if (from && to && to > from) {
        spans.set(id, { id, from, to, laneOrder: 1, data: { kind: 'transport', itemId: option.itemId, label, itemType, colours } })
        continue
      }

      /*
       * One day is not a stretch to draw across. Half of it either side of the
       * midpoint would be a sliver, and the whole tile would claim a stay that
       * never happened -- so the day is marked instead of spanned.
       */
      const day = getTransportOptionDay(option, connection, stopDaysById)
      if (!day) continue

      const marker: TransportMarker = { id, itemId: option.itemId, date: day, label, itemType, colours }
      const existing = markersByDate.get(day)
      if (existing) {
        if (!existing.some(other => other.id === id)) existing.push(marker)
      } else {
        markersByDate.set(day, [marker])
      }
    }
  }

  return { spans: [...spans.values()], markersByDate }
}

/**
 * What is already planned on each date, keyed by ISO date, so a day tile can
 * show the experiences and bookings that live on it.
 */
export function buildStopCalendarChips(
  stops: ReadonlyArray<TripStop> | undefined,
): Map<string, Array<StopCalendarChip>> {
  const chipsByDate = new Map<string, Array<StopCalendarChip>>()
  if (!stops?.length) return chipsByDate

  for (const stop of stops) {
    if (stop.stopType === 'ORIGIN') continue

    for (const day of stop.days) {
      const dayDate = day.date
      if (!dayDate || !day.items?.length) continue

      // A multi-day stay repeats across its nights; only the arrival earns a chip.
      const chips = day.items
        .filter(item => 'isBooked' in item && item.isBooked)
        .filter(item => !getBookedAccommodationDateRange(item, dayDate))
        .filter(item => (item.totalDays ?? 1) <= 1 || item.dayOfStay === 1 || item.isCheckIn === true)
        .map(toChip)

      if (!chips.length) continue

      const existing = chipsByDate.get(dayDate)
      if (existing) {
        existing.push(...chips)
      } else {
        chipsByDate.set(dayDate, chips)
      }
    }
  }

  return chipsByDate
}
